import React from 'react';
import { connect } from 'react-redux';
import Logger, {LOG_LEVELS} from '../../../utils/logger';
import ReactTable from "react-table";
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import Pagination from '../../page/Pagination'
import FormattedCurrencyValue from '../../fields/number/FormattedCurrencyValue';
import LANGUAGES_CONTROLLER from '../../../controllers/languages';
import { withFullStoryExclude } from '../../../utils/fullStoryHelper';
import LocalListingVATImportSection from '../localListing/LocalListingVATImportSection';
import { isLocalListingImportEnabled } from '../../../utils/localListingImportConfig';

const log = new Logger('EDITABLE_LOCAL_LISTING_LINES', LOG_LEVELS.DEBUG);

/** Constant for Sales listing type (Out) */
const LISTING_TYPE_SALES = 0;

/**
 * Delete button cell renderer - defined outside component to avoid recreation on each render.
 * This prevents React from destroying/recreating DOM nodes on every render.
 * 
 * @param {object} props - Component props
 * @param {object} props.row - The row data from ReactTable
 * @param {object} props.original - The original row values
 * @param {number} props.index - The row index
 * @param {function} props.onDeleteHandler - Handler function for delete action
 */
const DeleteButtonCell = ({ row, original, index, onDeleteHandler }) => {
    return (
        <button className="tertiary" onClick={() => onDeleteHandler(row, original, index)}>
            <LanguageStringResolver string='editableListingLines_Delete'/>
        </button>
    );
};

/**
 * Column header components - defined outside component to avoid recreation on each render.
 * This prevents React from destroying/recreating DOM nodes on every render.
 */
const VATNumberHeader = () => <LanguageStringResolver string='localListingFilingReportType_VATNumber'/>;
const TotalAmountHeader = () => <LanguageStringResolver string='localListingFilingReportType_TotalAmount'/>;
const AmountExclVATHeader = () => <LanguageStringResolver string='localListingFilingReportType_TotalAmountExclVAT'/>;
const AmountVATHeader = () => <LanguageStringResolver string='localListingFilingReportType_TotalAmountVAT'/>;
const InOutHeader = () => <LanguageStringResolver string='localListingFilingReportType_InOut'/>;
const DeleteHeader = () => <LanguageStringResolver string='editableListingLines_Delete'/>;

/**
 * EditableLocaListingLines - Component for displaying and editing Local Listing data in a grid.
 * Displays VAT numbers with their associated amounts and allows deletion of rows.
 * 
 * @class EditableLocaListingLines
 * @extends React.Component
 */
class EditableLocaListingLines extends React.Component {

    constructor(props) {
        super(props);
        this.handleDelete = this.handleDelete.bind(this);
        this.renderDeleteCell = this.renderDeleteCell.bind(this);
        this.handleVATImportData = this.handleVATImportData.bind(this);
    }

    /**
     * Handles delete button click - passes row data to parent component.
     * 
     * @param {object} row - The row data from ReactTable
     * @param {object} original - The original row values
     * @param {number} index - The row index to delete
     */
    handleDelete(row, original, index) {
        this.props.onDelete(row, original, index, false, null, this.props.filingTypeId);
    }

    /**
     * Renders the delete cell - wrapper to pass handler to DeleteButtonCell.
     * 
     * @param {object} cellProps - Cell props from ReactTable
     * @param {object} cellProps.row - The row data
     * @param {object} cellProps.original - The original row values
     * @param {number} cellProps.index - The row index
     * @returns {JSX.Element} DeleteButtonCell component
     */
    renderDeleteCell({ row, original, index }) {
        return <DeleteButtonCell row={row} original={original} index={index} onDeleteHandler={this.handleDelete} />;
    }

    /**
     * Handles imported VAT data from the LocalListingVATImportSection.
     * Clears existing data and replaces with imported data.
     * 
     * @param {Array} importedLines - Array of line items from the imported file
     */
    handleVATImportData(importedLines) {
        log.debug('handleVATImportData: Received lines', importedLines.length);
        if (typeof this.props.onVATImport === 'function') {
            this.props.onVATImport(importedLines);
        }
    }

    render() {
        const filingValues = (this.props.localListingData) || [];
        const localListingData = filingValues.map((localListingEntry) => {
            let inOut = "";
            if (localListingEntry.listingLineType === LISTING_TYPE_SALES) {
                inOut = LANGUAGES_CONTROLLER.format(this.props.languages, 'localListingFilingReportType_Out');
            } else {
                inOut = LANGUAGES_CONTROLLER.format(this.props.languages, 'localListingFilingReportType_In');
            }

            return {
                vatNumber: localListingEntry.vatNumber,
                totalAmount: localListingEntry.totalAmount || 0.0,
                amountExclVAT: localListingEntry.amountExclVAT || 0.0,
                amountVAT: localListingEntry.amountVAT || 0.0,
                inOut: inOut
            };
        });
        const getLocalListingCols = () => {
            return [{
                Header: VATNumberHeader,
                accessor: 'vatNumber',
                filterable: true
            }, {
                Header: TotalAmountHeader,
                id: 'totalAmount',
                accessor: d =>  <FormattedCurrencyValue value={d.totalAmount} />,
                filterable: true,
                className: withFullStoryExclude()
            },
                {
                    Header: AmountExclVATHeader,
                    id: 'amountExclVAT',
                    accessor: d =>  <FormattedCurrencyValue value={d.amountExclVAT} />,
                    filterable: true,
                    className: withFullStoryExclude(),
                    headerClassName: withFullStoryExclude()
                },
                {
                    Header: AmountVATHeader,
                    id: 'amountVAT',
                    accessor: d =>  <FormattedCurrencyValue value={d.amountVAT} />,
                    filterable: true,
                    className: withFullStoryExclude(),
                    headerClassName: withFullStoryExclude()
                },
                {
                    Header: InOutHeader,
                    id : 'listingLineType',
                    accessor: 'inOut',
                    filterable: true
                },
                {
                    id: "checkbox",
                    accessor: "SendingEnabled",
                    Cell: this.renderDeleteCell,
                    Header: DeleteHeader,
                    className: 'text-align-center',
                    width: 100,
                    sortable: false
                }
            ]
        };
        // Check if VAT Import section should be shown
        const showVATImportSection = isLocalListingImportEnabled(
            this.props.countryCode,
            this.props.featureFlags
        );

        return (
            <div>
                <ReactTable
                    data={localListingData}
                    columns={getLocalListingCols()}
                    defaultPageSize={10}
                    showPageJump={false}
                    showPagination={true}
                    PaginationComponent={Pagination}
                />

                {/* VAT Import Section - Only shown for supported countries */}
                {showVATImportSection && (
                    <LocalListingVATImportSection onDataImported={this.handleVATImportData} />
                )}
            </div>
        );
    }
}


EditableLocaListingLines.defaultProps = {
    localListingData: [],
    onDelete: () => {},
    countryCode: '',
    onVATImport: () => {},
    featureFlags: {}
};

function mapStateToProps(state) {
    log.debug('mapStateToProps: ', state);
    return {
        languages: state.languages,
        featureFlags: state.featureFlag?.data || {}
    };
}

export default connect(mapStateToProps)(EditableLocaListingLines);
